import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Badge } from '../core/Badge';
import { Button } from '../core/Button';
import { Icon } from '../core/Icon';
import { Alert } from '../feedback/Alert';
import { Select } from '../forms/Select';

export interface CampoDeImportacao {
    /** Chave do campo no destino. */
    chave: string;
    rotulo: string;
    /** Exemplo do formato esperado, ex.: "(11) 99999-9999". */
    exemplo?: string;
    obrigatorio?: boolean;
    /** Nomes alternativos usados no pré-mapeamento. */
    sinonimos?: string[];
}

/** Campo da Fórmula MED → nome da coluna na planilha. */
export type MapaDeImportacao = Record<string, string>;

export interface ImportMapperProps {
    arquivo?: string;
    colunas?: string[];
    /** Primeiras linhas da planilha, na ordem das colunas. */
    amostra?: string[][];
    campos?: CampoDeImportacao[];
    onChange?: (mapa: MapaDeImportacao) => void;
    onConfirmar?: (mapa: MapaDeImportacao) => void;
    onCancelar?: () => void;
    style?: CSSProperties;
}

/** Aproxima nome de coluna e nome de campo, para pré-mapear o óbvio. */
function normalizar(v: string): string {
    return v.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]/g, '');
}

function sugerir(colunas: string[], campos: CampoDeImportacao[]): MapaDeImportacao {
    const mapa: MapaDeImportacao = {};
    const usadas = new Set<string>();
    for (const campo of campos) {
        const alvos = [campo.chave, campo.rotulo, ...(campo.sinonimos ?? [])].map(normalizar);
        const achou = colunas.find((c) => !usadas.has(c) && alvos.some((a) => {
            const n = normalizar(c);
            return n === a || n.includes(a) || a.includes(n);
        }));
        if (achou) {
            mapa[campo.chave] = achou;
            usadas.add(achou);
        }
    }
    return mapa;
}

/**
 * Importação com mapeamento manual de campos.
 *
 * Planilha de clínica nunca vem com o cabeçalho que o sistema espera: vem
 * "Celular", "Fone 1", "Nome do Paciente". Importar por posição de coluna, ou
 * exigir que a pessoa renomeie o cabeçalho antes, é o que faz a importação
 * falhar e o cadastro nascer sujo.
 *
 * A coluna é arrastada para o campo de destino, e o sistema pré-mapeia o que
 * reconhece por nome. Arrastar não é acessível sozinho: cada coluna também é um
 * botão — foco, Enter, e o campo recebe pelo clique ou pela lista.
 */
export function ImportMapper({
    arquivo, colunas = [], amostra = [], campos = [], onChange, onConfirmar, onCancelar, style,
}: ImportMapperProps) {
    const [mapa, setMapa] = useState<MapaDeImportacao>(() => sugerir(colunas, campos));
    const [arrastando, setArrastando] = useState<string | null>(null);
    const [sobre, setSobre] = useState<string | null>(null);
    const [selecionada, setSelecionada] = useState<string | null>(null);

    useEffect(() => {
        onChange?.(mapa);
        /* `onChange` fora das dependências de propósito: função nova a cada
           render do pai relançaria o efeito em laço. */
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapa]);

    const usadas = new Set(Object.values(mapa));
    const obrigatoriosFaltando = campos.filter((c) => c.obrigatorio && !mapa[c.chave]);
    const naoMapeadas = colunas.filter((c) => !usadas.has(c));

    const vincular = (chaveCampo: string, coluna: string) => {
        setMapa((m) => {
            const novo = { ...m };
            /* Uma coluna serve a um campo só: duas vezes a mesma coluna é erro
               de mapeamento, não intenção. */
            for (const k of Object.keys(novo)) if (novo[k] === coluna) delete novo[k];
            novo[chaveCampo] = coluna;
            return novo;
        });
        setSelecionada(null);
    };

    const desvincular = (chaveCampo: string) => setMapa((m) => {
        const novo = { ...m };
        delete novo[chaveCampo];
        return novo;
    });

    const valorAmostra = (coluna: string | undefined): string => {
        if (!coluna) return '';
        const i = colunas.indexOf(coluna);
        return String(amostra[0]?.[i] ?? '');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, ...style }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                <Icon name="file-spreadsheet" size={17} style={{ color: 'var(--neutral-600)' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>{arquivo ?? 'planilha.xlsx'}</span>
                    <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                        {colunas.length} colunas · {amostra.length} linhas de amostra
                    </span>
                </span>
                <Badge tone={obrigatoriosFaltando.length ? 'warning' : 'success'} dot>
                    {Object.keys(mapa).length} de {campos.length} campos mapeados
                </Badge>
            </div>

            {obrigatoriosFaltando.length > 0 && (
                <Alert tone="warning" title={`Faltam ${obrigatoriosFaltando.length} campos obrigatórios`} dismissible={false}>
                    {obrigatoriosFaltando.map((c) => c.rotulo).join(', ')}. Arraste a coluna correspondente ou escolha na lista.
                </Alert>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '228px minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
                <div>
                    <p style={{ margin: '0 0 9px', fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                        Colunas da planilha
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
                        {colunas.map((c) => {
                            const usada = usadas.has(c);
                            const escolhida = selecionada === c;
                            return (
                                <button
                                    key={c} type="button" draggable
                                    aria-pressed={escolhida}
                                    onDragStart={() => setArrastando(c)}
                                    onDragEnd={() => { setArrastando(null); setSobre(null); }}
                                    onClick={() => setSelecionada(escolhida ? null : c)}
                                    title={usada ? 'Já mapeada' : 'Arraste para um campo, ou selecione e escolha o campo'}
                                    style={{
                                        padding: '8px 10px', borderRadius: 'var(--radius-sm)', cursor: 'grab', textAlign: 'left',
                                        background: escolhida ? 'var(--surface-brand-soft)' : usada ? 'var(--surface-secondary)' : 'var(--surface)',
                                        border: `1px solid ${escolhida ? 'var(--burgundy-700)' : usada ? 'var(--border-subtle)' : 'var(--border-strong)'}`,
                                        opacity: arrastando === c ? 0.5 : 1,
                                        transition: 'var(--transition-control)',
                                    }}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                                        <Icon name="grip-vertical" size={13} style={{ color: 'var(--neutral-400)', flex: '0 0 auto' }} />
                                        <span style={{ flex: 1, minWidth: 0, fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: usada ? 'var(--text-secondary)' : 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</span>
                                        {usada && <Icon name="check" size={13} style={{ color: 'var(--success-strong)', flex: '0 0 auto' }} />}
                                    </span>
                                    <span style={{ display: 'block', marginTop: 2, paddingLeft: 20, fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {valorAmostra(c) || '—'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    {naoMapeadas.length > 0 && (
                        <p style={{ margin: '10px 0 0', fontFamily: 'var(--font-ui)', fontSize: 12, lineHeight: '17px', color: 'var(--text-tertiary)' }}>
                            {naoMapeadas.length} {naoMapeadas.length === 1 ? 'coluna será ignorada' : 'colunas serão ignoradas'} na importação.
                        </p>
                    )}
                </div>

                <div>
                    <p style={{ margin: '0 0 9px', fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                        Campos da Fórmula MED
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto', paddingRight: 4 }}>
                        {campos.map((campo) => {
                            const col = mapa[campo.chave];
                            const ativo = sobre === campo.chave;
                            return (
                                <div
                                    key={campo.chave}
                                    onDragOver={(e) => { e.preventDefault(); setSobre(campo.chave); }}
                                    onDragLeave={() => setSobre(null)}
                                    onDrop={(e) => { e.preventDefault(); if (arrastando) vincular(campo.chave, arrastando); setSobre(null); setArrastando(null); }}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 12, padding: '9px 12px',
                                        borderRadius: 'var(--radius-sm)',
                                        background: ativo ? 'var(--surface-brand-soft)' : col ? 'var(--surface)' : 'var(--surface-secondary)',
                                        border: ativo ? '1.5px solid var(--burgundy-700)'
                                            : col ? '1px solid var(--border)'
                                                : `1.5px dashed ${campo.obrigatorio ? 'var(--warning)' : 'var(--border-strong)'}`,
                                        transition: 'var(--transition-control)',
                                    }}
                                >
                                    <span style={{ width: 168, flex: '0 0 auto' }}>
                                        <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
                                            {campo.rotulo}{campo.obrigatorio && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}
                                        </span>
                                        {campo.exemplo && (
                                            <span style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 11.5, color: 'var(--text-tertiary)' }}>{campo.exemplo}</span>
                                        )}
                                    </span>

                                    <span style={{ flex: 1, minWidth: 0 }}>
                                        {col ? (
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, maxWidth: '100%', height: 28, padding: '0 6px 0 9px', borderRadius: 'var(--radius-xs)', background: 'var(--surface-brand-soft)', border: '1px solid var(--burgundy-100)' }}>
                                                <span style={{ minWidth: 0, fontFamily: 'var(--font-ui)', fontSize: 12.5, fontWeight: 600, color: 'var(--text-brand)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{col}</span>
                                                <button
                                                    type="button" aria-label={`Remover mapeamento de ${campo.rotulo}`}
                                                    onClick={() => desvincular(campo.chave)}
                                                    style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--burgundy-600)', padding: 2, display: 'flex' }}
                                                >
                                                    <Icon name="x" size={13} />
                                                </button>
                                            </span>
                                        ) : (
                                            /* Caminho por teclado: arrastar não basta. */
                                            <Select
                                                value="" onChange={(v) => vincular(campo.chave, v)}
                                                aria-label={`Coluna para ${campo.rotulo}`}
                                                placeholder={selecionada ? `Vincular “${selecionada}”` : 'Escolha a coluna'}
                                                options={colunas.map((c) => ({ value: c, label: c }))} size="sm"
                                            />
                                        )}
                                    </span>

                                    {col && (
                                        <span style={{ width: 150, flex: '0 0 auto', fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {valorAmostra(col) || '—'}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {Object.keys(mapa).length > 0 && (
                <div>
                    <p style={{ margin: '0 0 9px', fontFamily: 'var(--font-ui)', fontSize: 11, fontWeight: 600, letterSpacing: 'var(--tracking-wide)', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
                        Prévia do que será importado
                    </p>
                    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-ui)', fontSize: 12.5 }}>
                            <thead>
                                <tr style={{ background: 'var(--surface-secondary)' }}>
                                    {campos.filter((c) => mapa[c.chave]).map((c) => (
                                        <th key={c.chave} style={{ textAlign: 'left', padding: '8px 11px', whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: 600, color: 'var(--text-secondary)', borderBottom: '1px solid var(--border)' }}>{c.rotulo}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {amostra.slice(0, 3).map((linha, i) => (
                                    <tr key={i}>
                                        {campos.filter((c) => mapa[c.chave]).map((c) => (
                                            <td key={c.chave} style={{ padding: '8px 11px', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-primary)' }}>
                                                {String(linha[colunas.indexOf(mapa[c.chave] ?? '')] ?? '—')}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {(onConfirmar ?? onCancelar) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
                    <Button variant="ghost" onClick={() => setMapa(sugerir(colunas, campos))} iconLeft={<Icon name="wand-sparkles" size={15} />}>
                        Remapear automaticamente
                    </Button>
                    <span style={{ marginLeft: 'auto' }} />
                    {onCancelar && <Button variant="secondary" onClick={onCancelar}>Cancelar</Button>}
                    {onConfirmar && (
                        <Button disabled={obrigatoriosFaltando.length > 0} onClick={() => onConfirmar(mapa)}>Importar contatos</Button>
                    )}
                </div>
            )}
        </div>
    );
}
